import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCheck, Loader2, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { oapi, fmtWhen } from '@/lib/officialsApi';
import { PayFilters } from './PayFilters';
import { Money, Panel, Loading } from './PayBits';

const CHOICES = [['worked', 'Worked'], ['no_show', 'Did not attend'], ['replaced', 'Replaced'], ['cancelled', 'Cancelled'], ['pending', 'Pending']];
const TONE = { worked: 'border-emerald-300 text-emerald-700', no_show: 'border-red-300 text-red-700', replaced: 'border-slate-300 text-slate-600',
  cancelled: 'border-slate-300 text-slate-600', pending: 'border-amber-300 text-amber-700' };

const LineRow = ({ l, currency, sel, onSel, choice, onChoice, note, onNote }) => (
  <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-t" data-testid={`verify-line-${l.id}`}>
    <Checkbox checked={sel} onCheckedChange={() => onSel(l.id)} data-testid={`verify-select-${l.id}`} />
    <div className="min-w-[160px] flex-1">
      <div className="font-semibold text-slate-800 text-sm">{l.official_name}</div>
      <div className="text-xs text-slate-500">{l.role_label} · <Money minor={l.amount_minor} currency={currency} /></div>
    </div>
    <select value={choice} onChange={e => onChoice(l.id, e.target.value)} data-testid={`verify-status-${l.id}`}
      className={`h-9 rounded-md border-2 px-2 text-sm font-semibold bg-white ${TONE[choice]}`}>
      {CHOICES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
    <Input value={note} onChange={e => onNote(l.id, e.target.value)} placeholder="Note (optional)" className="h-9 w-full sm:w-48" data-testid={`verify-note-${l.id}`} />
  </div>
);

export const VerifyQueue = ({ orgId, currency, perms, competitions, officials, roles, tick, refresh }) => {
  const [f, setF] = useState({});
  const [lines, setLines] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [choice, setChoice] = useState({});
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    oapi('/officials/payments/ledger', { orgId, query: { ...f, status: 'pending_verification' } })
      .then(d => { setLines(d.lines); setSel(new Set()); }).catch(e => toast.error(e.message));
  }, [orgId, f]);
  useEffect(() => { load(); }, [load, tick]);
  const games = useMemo(() => {
    const m = new Map();
    (lines || []).forEach(l => { const k = `${l.event_id}:${l.match_id}`; if (!m.has(k)) m.set(k, []); m.get(k).push(l); });
    return [...m.values()];
  }, [lines]);
  const toggle = (id) => setSel(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const submit = async (ids) => {
    setBusy(true);
    try {
      const r = await oapi('/officials/payments/verify', { method: 'POST', orgId, body: { items: ids.map(id => ({ line_id: id, status: choice[id] || 'worked', note: notes[id] || '' })) } });
      toast.success(`${r.updated} verified${r.skipped.length ? ` · ${r.skipped.length} skipped: ${r.skipped[0].reason}` : ''}`);
      refresh();
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const can = perms.has('verify');
  return (
    <div className="space-y-4" data-testid="pay-verify">
      <PayFilters f={f} setF={setF} competitions={competitions} officials={officials} roles={roles} showStatus={false} />
      <Panel title="Verify completed assignments" desc="Confirm who actually worked each played game. Only 'Worked' becomes payable." testId="verify-panel"
        actions={can && sel.size > 0 && (
          <Button size="sm" disabled={busy} onClick={() => submit([...sel])} className="bg-emerald-600 hover:bg-emerald-700" data-testid="verify-apply-selected">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-1.5" />}Save {sel.size} selected
          </Button>
        )}>
        {!lines ? <Loading /> : !games.length ? (
          <div className="py-12 text-center" data-testid="verify-empty"><ClipboardCheck className="w-8 h-8 mx-auto text-emerald-500" />
            <p className="mt-2 text-sm text-slate-600">Nothing to verify. Played games show up here automatically.</p></div>
        ) : games.map(ls => {
          const g = ls[0];
          return (
            <div key={`${g.event_id}:${g.match_id}`} className="border-t first:border-t-0" data-testid={`verify-game-${g.match_id}`}>
              <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-slate-50">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-bold text-slate-900 text-sm">{g.game?.team_a_name} vs {g.game?.team_b_name}</div>
                  <div className="text-xs text-slate-500">{fmtWhen(g.game)} · {g.game?.event_name}</div>
                </div>
                {can && <Button size="sm" variant="outline" disabled={busy} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => { ls.forEach(l => setChoice(c => ({ ...c, [l.id]: c[l.id] || 'worked' }))); submit(ls.map(l => l.id)); }}
                  data-testid={`verify-game-save-${g.match_id}`}>Save this game</Button>}
              </div>
              {ls.map(l => <LineRow key={l.id} l={l} currency={currency} sel={sel.has(l.id)} onSel={toggle} choice={choice[l.id] || 'worked'}
                onChoice={(id, v) => setChoice(c => ({ ...c, [id]: v }))} note={notes[l.id] || ''} onNote={(id, v) => setNotes(n => ({ ...n, [id]: v }))} />)}
            </div>
          );
        })}
      </Panel>
    </div>
  );
};
